/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useRef, useState } from 'react';
import useAppDispatch from 'hooks/useAppDispatch';

import { Fab, Box, Button, CircularProgress, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import Page from 'components/templates/Page';
import { Form, Formik } from 'formik';
import ImageEditor from 'modals/CropImage';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import { closeSnackbar, useSnackbar } from 'notistack';
import { ROUTES } from 'utils/constants';
import { useNavigate } from 'react-router-dom';
import { getCompanyAction } from 'store/modules/auth/actions';
import { getCookieSessionToken } from 'utils/session';
import { CompanyService } from 'services/company.service';
import { CompanyType } from 'types/company';
import { useStyles } from './styles';

function Career() {
  const styles = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [userCompany, setUserCompany] = useState<CompanyType>();
  const companyId = userCompany?.id;

  const imageInput = useRef(null);

  const [openEditorProfile, setOpenEditorProfile] = useState(false);
  const [imageProfile, setImageProfile] = useState(null);

  let imageCover = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/cover.png`;
  let imageAvatar = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/avatar-small.png`;

  const { enqueueSnackbar } = useSnackbar();
  const MINIMUM_SIZE = 50;

  const redirectUser = () => {
    setLoading(true);
    navigate(`${ROUTES.SLOGAN}`);
    setLoading(false);
  };

  const title = (
    <span>
      Configure a
      <br />
      página de carreiras
    </span>
  );

  const action = (key: number | string) => (
    <Button
      onClick={() => {
        closeSnackbar(key);
      }}
      style={{ fontFamily: 'Nunito', color: '#ffffff' }}
    >
      Entendi
    </Button>
  );

  function readFile(file: any) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  }

  const handleEditedImage = async (file: any) => {
    enqueueSnackbar('Atualizando a marca da empresa...', {
      variant: 'info'
    });

    try {
      if (file !== 'undefined') {
        setLoading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('_method', 'PATCH');

        await CompanyService.changeAvatar(formData as any, companyId ?? '');
        await CompanyService.updateCompany(companyId ?? '', {
          ...userCompany,
          hasAvatar: true
        });
        dispatch(getCompanyAction(getCookieSessionToken() ?? ''));
        enqueueSnackbar('Marca atualizada.', {
          variant: 'success'
        });
        imageAvatar = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/avatar-small.png`;
        window.location.reload();
      }
    } catch (err) {
      enqueueSnackbar('Não foi possível atualizar a marca da empresa.', {
        variant: 'error',
        persist: true,
        action: (key) => action(key)
      });
    } finally {
      setOpenEditorProfile(false);
      setLoading(false);
    }
  };

  const handleImageUploadBanner = async ({ target: { files } }: any) => {
    enqueueSnackbar('Atualizando a imagem da capa...', {
      variant: 'info'
    });

    try {
      setLoading(true);
      await CompanyService.changeCover(files[0], companyId ?? '');
      dispatch(getCompanyAction(getCookieSessionToken() ?? ''));
      enqueueSnackbar('Capa atualizada.', {
        variant: 'success'
      });
      await CompanyService.updateCompany(companyId ?? '', {
        ...userCompany,
        hasCover: true
      });
      imageCover = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/cover.png`;
      window.location.reload();
    } catch (err) {
      enqueueSnackbar('Não foi possível atualizar a imagem da capa', {
        variant: 'error',
        persist: true,
        action: (key) => action(key)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploadProfile = ({ target: { files } }: any) => {
    setLoading(true);
    try {
      if (files.length === 1) {
        const img = document.createElement('img');
        const blob = URL.createObjectURL(files[0]);
        img.src = blob;
        img.onload = async function () {
          if (img.width >= MINIMUM_SIZE && img.height >= MINIMUM_SIZE) {
            const imageDataUrl = (await readFile(files[0])) as any;
            setImageProfile(imageDataUrl);
            setOpenEditorProfile(true);
          } else {
            enqueueSnackbar(
              `A imagem precisa ter uma resolução superior à ${MINIMUM_SIZE}x${MINIMUM_SIZE}`,
              {
                variant: 'error',
                persist: true,
                action: (key) => action(key)
              }
            );
          }
        };
      }
    } catch (err) {
      enqueueSnackbar('Não foi possível carregar a imagem', {
        variant: 'error',
        persist: true,
        action: (key) => action(key)
      });
    }
    setLoading(false);
  };

  const handleChangeImage = () => {
    if (imageInput?.current) {
      const inputElement = imageInput.current as HTMLInputElement;
      inputElement.value = '';
    }
  };

  const getInfo = async () => {
    setLoading(true);
    try {
      const { data } = await CompanyService.getAssociatedCompany();
      setUserCompany(data[0]?.company);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInfo();
  }, [imageCover, imageAvatar]);

  return (
    <Page title={title} subtitle="">
      <Formik initialValues={{}} onSubmit={() => {}}>
        {({ handleSubmit }) => (
          <Form className={styles.container} onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap="8px">
              <Box className={styles.containerBox}>
                {!userCompany?.hasCover ? (
                  <Box className={styles.grayDiv} />
                ) : (
                  <img src={imageCover} alt="img" className={styles.grayDiv} />
                )}
                {!userCompany?.hasAvatar ? (
                  <Box className={styles.imageIcon}>
                    <BusinessIcon sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
                  </Box>
                ) : (
                  <img src={imageAvatar} alt="img" className={styles.image} />
                )}
                <Typography className={styles.company}>
                  {userCompany?.fantasyName}
                </Typography>
                <Box className={styles.informations}>
                  <SellOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.industry?.name}
                  </Typography>
                </Box>
                <Box className={styles.informations}>
                  <PlaceOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.city}, {userCompany?.state}
                  </Typography>
                </Box>
                <Box
                  className={styles.informations}
                  style={{ marginBottom: '12px' }}
                >
                  <GroupWorkOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.amountIntervalWorkers}
                  </Typography>
                </Box>
              </Box>
              {!userCompany?.hasCover && (
                <>
                  <input
                    id="banner-input"
                    className={styles.input}
                    ref={imageInput}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUploadBanner}
                  />
                  <label htmlFor="banner-input">
                    <Button
                      component="span"
                      fullWidth
                      variant="outlined"
                      style={{ height: '40px', marginTop: '25px' }}
                      onClick={handleChangeImage}
                    >
                      selecione a imagem da capa
                    </Button>
                  </label>
                </>
              )}
              {!userCompany?.hasAvatar && (
                <>
                  <input
                    id="image-input"
                    className={styles.input}
                    ref={imageInput}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUploadProfile}
                  />
                  <label htmlFor="image-input">
                    <Button
                      component="span"
                      fullWidth
                      variant="outlined"
                      style={{ height: '40px' }}
                      onClick={handleChangeImage}
                    >
                      selecione a marca da empresa
                    </Button>
                  </label>
                </>
              )}
            </Box>
            <Box className={styles.stepsContainer}>
              <Button variant="text" onClick={redirectUser} disabled={loading}>
                Pular
              </Button>

              <Fab
                type="submit"
                disabled={loading}
                style={{ width: '56px', height: '56px' }}
                onClick={redirectUser}
              >
                {loading ? (
                  <CircularProgress style={{ color: '#fff' }} size={24} />
                ) : (
                  <ArrowForward />
                )}
              </Fab>
            </Box>
          </Form>
        )}
      </Formik>
      <ImageEditor
        open={openEditorProfile}
        onClose={() => setOpenEditorProfile(false)}
        onFinish={(file) => handleEditedImage(file)}
        onDisableLoading={() => setLoading(false)}
        imagem={imageProfile}
      />
    </Page>
  );
}

export default Career;
